'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { IdentityInfoSchemaType } from '@/types/portal-api.generated'
import type { IdentityScopes } from '@/utilities/auth/identityScopeRoles'
import {
  fetchTrustedIdentityScopes,
  getCachedTrustedIdentityScopes,
  setCachedTrustedIdentityScopesFromIdentity,
} from '@/utilities/auth/trustedIdentityScopes'
import { USER_IDENTITY_SET_EVENT } from '@/utilities/client/userIdentityEvent'
import userService from '@/utilities/axios/api/user'

export type UseTrustedIdentityScopesOptions = {
  /** When false, skip fetching and identity-event handling. Default true. */
  enabled?: boolean
  /** Prefix for fetch-failure console errors. */
  errorMessage?: string
}

export type UseTrustedIdentityScopesResult = {
  scopes: IdentityScopes | null
  /**
   * True once a cache hit, fetch success, or fetch failure has been observed.
   * Callers decide fail-open vs fail-closed when `scopes` is null.
   */
  isReady: boolean
  /** Increments whenever scopes are (re)applied so dependent effects can re-run. */
  scopesEpoch: number
}

/**
 * Shared identity-scope loader: cache → fetch → USER_IDENTITY_SET_EVENT, with stale-request guards.
 * On failure, becomes ready with `scopes: null` so callers can choose fail-open or fail-closed.
 */
export function useTrustedIdentityScopes(
  options: UseTrustedIdentityScopesOptions = {},
): UseTrustedIdentityScopesResult {
  const { enabled = true, errorMessage = 'Error loading identity scopes:' } = options
  const cached = getCachedTrustedIdentityScopes()
  const [scopes, setScopes] = useState<IdentityScopes | null>(() => (enabled ? cached : null))
  const [isReady, setIsReady] = useState(() => (enabled ? cached !== null : false))
  const [scopesEpoch, setScopesEpoch] = useState(0)
  const latestRequestRef = useRef(0)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const applyScopes = useCallback((next: IdentityScopes | null, ready: boolean) => {
    setScopes(next)
    setIsReady(ready)
    setScopesEpoch((n) => n + 1)
  }, [])

  const ensureScopes = useCallback(
    (opts?: { identity?: IdentityInfoSchemaType }) => {
      if (!enabled) return

      const requestId = ++latestRequestRef.current
      const providedIdentity = opts?.identity

      if (providedIdentity !== undefined) {
        applyScopes(setCachedTrustedIdentityScopesFromIdentity(providedIdentity), true)
        return
      }

      const fromCache = getCachedTrustedIdentityScopes()
      if (fromCache) {
        applyScopes(fromCache, true)
        return
      }

      setIsReady(false)

      void fetchTrustedIdentityScopes(() => userService.getIdentityInfo())
        .then((loaded) => {
          if (!isMountedRef.current || requestId !== latestRequestRef.current) return
          applyScopes(loaded, true)
        })
        .catch((error) => {
          if (!isMountedRef.current || requestId !== latestRequestRef.current) return
          console.error(errorMessage, error)
          applyScopes(null, true)
        })
    },
    [enabled, errorMessage, applyScopes],
  )

  useEffect(() => {
    if (!enabled) return
    ensureScopes()
  }, [enabled, ensureScopes])

  useEffect(() => {
    if (typeof window === 'undefined' || !enabled) return

    const handleUserIdentitySet = (event: Event) => {
      const identity = (event as CustomEvent<{ identity?: IdentityInfoSchemaType } | undefined>)
        .detail?.identity
      ensureScopes(identity === undefined ? undefined : { identity })
    }

    window.addEventListener(USER_IDENTITY_SET_EVENT, handleUserIdentitySet)
    return () => window.removeEventListener(USER_IDENTITY_SET_EVENT, handleUserIdentitySet)
  }, [enabled, ensureScopes])

  return { scopes, isReady, scopesEpoch }
}
